import { enqueueSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { SupplyChainContext } from 'features/globalSetup/contexts/SupplyChainContext'
import { Schema } from 'features/globalSetup/pages/SupplyChainAdd/schema'
import { mapGraphQLErrors } from 'providers/graphql'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { ROUTES } from 'routes'
import { AddSchema } from '../../schemas/addSchema'
import { CREATE_SUPPLY_CHAIN } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseCreateSupplyChain, UseCreateSupplyChainProps } from './types'

export const useCreateSupplyChain = ({
  workspaceId,
}: UseCreateSupplyChainProps): UseCreateSupplyChain => {
  const navigate = useNavigate()
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'supplyChain.general.actions.create',
  })
  const { setLoading } = useContext(SupplyChainContext)
  const [errors, setErrors] = useState<FieldErrors<AddSchema>>({})

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: CREATE_SUPPLY_CHAIN,
    onCompleted: async (data: Data): Promise<void> => {
      const {
        createSupplyChain: { errors, supplyChain },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('error'), { variant: 'error' })
      } else if (supplyChain) {
        setErrors({})
        enqueueSnackbar(t('success', { supplyChain: supplyChain.name }), { variant: 'success' })
        navigate(generatePath(ROUTES.GLOBAL_SETUP.SCHAIN.LIST, { workspaceId }))
      }
    },
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const create = async (data: Schema): Promise<void> => {
    await mutateAsync({
      input: { ...data, workspaceId },
    })
  }

  return { create, errors }
}
